export type GlobalSearchResolveContext = {
  /** Current location; shared param names (`eventSlug`, `jobPostId`) only fill matching route trees. */
  pathname?: string;
  pgaId?: string;
  sectionId?: string;
  facilityId?: string;
  eventSlug?: string;
  jobPostId?: string;
  universityId?: string;
  activityTypeId?: string;
  activityId?: string;
};

const PARAM_KEYS = [
  "pgaId",
  "sectionId",
  "facilityId",
  "eventSlug",
  "jobPostId",
  "universityId",
  "activityTypeId",
  "activityId"
] as const;

export type GlobalSearchParamKey = (typeof PARAM_KEYS)[number];

const PARAM_KEY_SET = new Set<string>(PARAM_KEYS);

/** First path segments under `/people` that are staff tools, not a person record. */
const PEOPLE_NON_ENTITY_SEGMENTS = new Set([
  "membership-services",
  "person-merge-purge",
  "applications",
  "member-elections",
  "create"
]);

function isGlobalSearchParamKey(key: string): key is GlobalSearchParamKey {
  return PARAM_KEY_SET.has(key);
}

function firstSegmentAfter(pathname: string, prefix: string): string | undefined {
  if (pathname === prefix || pathname === `${prefix}/`) return undefined;
  if (!pathname.startsWith(`${prefix}/`)) return undefined;
  return pathname.slice(prefix.length + 1).split("/")[0];
}

function requiredParams(path: string): string[] {
  return [...path.matchAll(/:([A-Za-z]+)/g)].map((match) => match[1]);
}

function routeParam(value: string | undefined): string | undefined {
  return value && value.length > 0 ? value : undefined;
}

/**
 * Build resolve context from the current route only. Do not copy section/university
 * ids from profile state, and do not reuse `eventSlug` / `jobPostId` across trees.
 */
export function buildGlobalSearchResolveContext(
  params: Readonly<Record<string, string | undefined>>,
  pathname: string
): GlobalSearchResolveContext {
  const context: GlobalSearchResolveContext = { pathname };
  for (const key of PARAM_KEYS) {
    context[key] = routeParam(params[key]);
  }
  return context;
}

function paramAppliesToDestination(path: string, key: GlobalSearchParamKey, pathname: string | undefined): boolean {
  if (!pathname) return true;

  if (key === "pgaId") {
    const segment = firstSegmentAfter(pathname, "/people");
    return segment !== undefined && !PEOPLE_NON_ENTITY_SEGMENTS.has(segment);
  }

  if (key === "sectionId") {
    return Boolean(firstSegmentAfter(pathname, "/sections"));
  }

  if (key === "facilityId") {
    return Boolean(firstSegmentAfter(pathname, "/facilities"));
  }

  if (key === "universityId") {
    return Boolean(firstSegmentAfter(pathname, "/education/universities"));
  }

  if (key === "activityTypeId") {
    return Boolean(firstSegmentAfter(pathname, "/activities/activity-types"));
  }

  if (key === "activityId") {
    return /^\/activities\/activity-types\/[^/]+\/activity\/[^/]+(?:\/|$)/.test(pathname);
  }

  if (key === "eventSlug") {
    if (path.includes("/activities/events/:eventSlug")) {
      const segment = firstSegmentAfter(pathname, "/activities/events");
      return Boolean(segment) && segment !== "new";
    }
    if (path.includes("/education/universities/") && path.includes("/events/:eventSlug")) {
      return /^\/education\/universities\/[^/]+\/events\/(?!new(?:\/|$))[^/]+/.test(pathname);
    }
    if (path.startsWith("/events/:eventSlug")) {
      const segment = firstSegmentAfter(pathname, "/events");
      return Boolean(segment) && segment !== "pat-results";
    }
    return false;
  }

  if (key === "jobPostId") {
    if (path.includes("/sections/:sectionId/jobs/job-post/:jobPostId")) {
      return /^\/sections\/[^/]+\/jobs\/job-post\/(?!create(?:\/|$))[^/]+/.test(pathname);
    }
    if (path.includes("/facilities/:facilityId/jobs/job-post/:jobPostId")) {
      return /^\/facilities\/[^/]+\/jobs\/job-post\/(?!create(?:\/|$))[^/]+/.test(pathname);
    }
    if (path.startsWith("/jobs/job-post/:jobPostId")) {
      return /^\/jobs\/job-post\/(?!create(?:\/|$))[^/]+/.test(pathname);
    }
    return false;
  }

  return true;
}

function fallbackForPath(path: string): string {
  if (path.startsWith("/people/:pgaId")) return "/people";
  if (path.startsWith("/sections/:sectionId")) return "/sections";
  if (path.startsWith("/facilities/:facilityId")) return "/facilities";
  if (path.startsWith("/activities/events/:eventSlug")) return "/activities/events";
  if (path.startsWith("/events/:eventSlug")) return "/events";
  if (path.startsWith("/jobs/job-post/:jobPostId")) return "/jobs";
  if (path.startsWith("/education/universities/:universityId")) return "/education/universities";
  if (path.startsWith("/activities/activity-types/:activityTypeId/activity/:activityId")) {
    return "/activities/activity-types";
  }
  if (path.startsWith("/activities/activity-types/:activityTypeId")) return "/activities/activity-types";
  return path.replace(/\/:[^/]+/g, "");
}

/**
 * Entity-relative pages (person Awards, section Contact, a job post’s applicants, …)
 * are only listed when every `:param` can be filled from the current URL.
 * Static catalog paths always remain available.
 */
export function globalSearchDestinationIsAvailable(path: string, context: GlobalSearchResolveContext): boolean {
  const required = requiredParams(path);
  if (required.length === 0) return true;

  return required.every((key) => {
    if (!isGlobalSearchParamKey(key)) return false;
    if (!context[key]) return false;
    return paramAppliesToDestination(path, key, context.pathname);
  });
}

/** Parameterized destinations that can be opened for the current entity. */
export function globalSearchDestinationIsInContext(path: string, context: GlobalSearchResolveContext): boolean {
  return requiredParams(path).length > 0 && globalSearchDestinationIsAvailable(path, context);
}

export function resolveGlobalSearchPath(
  path: string,
  context: GlobalSearchResolveContext,
  fallbackPath?: string
): string {
  const required = requiredParams(path);
  if (required.length === 0) return path;

  let resolved = path;
  for (const key of required) {
    if (!isGlobalSearchParamKey(key)) {
      return fallbackPath ?? fallbackForPath(path);
    }
    if (!paramAppliesToDestination(path, key, context.pathname)) {
      return fallbackPath ?? fallbackForPath(path);
    }
    const value = context[key];
    if (!value) return fallbackPath ?? fallbackForPath(path);
    resolved = resolved.replace(new RegExp(`:${key}\\b`), encodeURIComponent(value));
  }

  return resolved;
}
